import { Cron } from '@modules/common/types';
import {
  getLocalCronValueFromUTCCronValue,
  getSavedEmailsFromReportSchedule,
  getSlackChannelsFromReportSchedule,
} from '@modules/common/utils';
import {
  CronValueInputV2,
  ScheduleViewFragment,
  SlackChannelInput,
} from 'generated/graphql';
import { isEqual } from 'lodash';

interface GetIsSaveButtonDisabledOptions {
  reportSchedule?: ScheduleViewFragment;
  cronSchedule?: Cron;
  selectedEmails: string[];
  selectedSlackChannels: SlackChannelInput[];
  isPDFReportingIncluded: boolean;
}

const isCronValuesEqual = (
  cronValue?: CronValueInputV2,
  savedCronValue?: CronValueInputV2,
) => {
  const isDayOfWeekEqual = isEqual(
    cronValue?.dayOfWeek,
    savedCronValue?.dayOfWeek,
  );

  const isHourOfDayEqual = isEqual(
    cronValue?.hourOfDay,
    savedCronValue?.hourOfDay,
  );

  const isDayOfMonthEqual = isEqual(
    cronValue?.dayOfMonth,
    savedCronValue?.dayOfMonth,
  );

  if (savedCronValue?.dayOfMonth || cronValue?.dayOfMonth) {
    return isDayOfWeekEqual && isHourOfDayEqual && isDayOfMonthEqual;
  }

  return isDayOfWeekEqual && isHourOfDayEqual;
};

export const getIsSaveButtonDisabled = ({
  reportSchedule,
  cronSchedule,
  selectedEmails,
  selectedSlackChannels,
  isPDFReportingIncluded,
}: GetIsSaveButtonDisabledOptions) => {
  const savedSlackChannels = getSlackChannelsFromReportSchedule(reportSchedule);
  const savedEmails = getSavedEmailsFromReportSchedule(reportSchedule);
  const savedPDFReportingOption =
    !reportSchedule?.metadata.defaultReportMetadata?.skipSnapshot;

  const savedCronValue = getLocalCronValueFromUTCCronValue(
    reportSchedule?.cron.valueV2,
  );

  const hasEmptyOutbounds =
    !selectedSlackChannels.length && !selectedEmails.length;

  if (hasEmptyOutbounds) {
    return true;
  }

  const hasSameRealtimeValue =
    !!cronSchedule?.realtime === !!reportSchedule?.cron.realtime;

  if (!hasSameRealtimeValue) {
    return false;
  }

  const isCronValueEqual = isCronValuesEqual(
    cronSchedule?.valueV2,
    savedCronValue,
  );

  if (!isCronValueEqual) {
    return false;
  }

  const isSameSlackChannels = isEqual(
    selectedSlackChannels,
    savedSlackChannels,
  );

  if (!isSameSlackChannels) {
    return false;
  }

  const isSameEmailValues = isEqual(selectedEmails, savedEmails);

  if (!isSameEmailValues) {
    return false;
  }

  const isSamePDFReportingOption =
    isPDFReportingIncluded === savedPDFReportingOption;

  return isSamePDFReportingOption;
};
